import { LockOutlined, LogoutOutlined, SettingOutlined, UserOutlined } from '@ant-design/icons';
import { history, useModel } from '@umijs/max';
import {message, Spin} from 'antd';
import type { MenuProps } from 'antd';
import { createStyles } from 'antd-style';
import { stringify } from 'querystring';
import React, { useState } from 'react';
import { flushSync } from 'react-dom';
import HeaderDropdown from '../HeaderDropdown';
import { UpdatingUserPasswordForm } from '@/components/RightContent/UpdatingPasswordModal';
import {Md5} from "ts-md5";
import {updateUserPassword} from "@/services/ant-design-pro/api";

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  return (
    <span className="anticon">
      {currentUser?.nickname ? currentUser?.nickname : currentUser?.username}
    </span>
  );
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({ menu, children }) => {

  const { styles } = useStyles();

  const { initialState, setInitialState } = useModel('@@initialState');

  const [updateModalOpen, handleUpdateModalOpen] = useState<boolean>(false);
  /**
   * 退出登录，并且将当前的 url 保存
   */
  const loginOut = async () => {
    const { search, pathname } = window.location;
    const urlParams = new URL(window.location.href).searchParams;
    /** 此方法会跳转到 redirect 参数所在的位置 */
    const redirect = urlParams.get('redirect');
    // Note: There may be security issues, please note
    if (window.location.pathname !== '/user/login' && !redirect) {
      history.replace({
        pathname: '/user/login',
        search: stringify({
          redirect: pathname + search,
        }),
      });
    }
  };

  //更新账号密码
  const handUpdatingPassword = async (fields: any) => {
    const hide = message.loading('Updating...');
    const params = {
      id: initialState!.currentUser!.id,
      password: Md5.hashStr(fields['password']),
    };
    try {
      await updateUserPassword({ ...params });
      hide();
      message.success('Updated Password successfully');
      return true;
    } catch (error) {
      hide();
      message.error('Updating failed, please try again!');
      return false;
    }
  };


  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      flushSync(() => {
        localStorage.removeItem('token');
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      loginOut();
      return;
    } else if (key === 'updating_password') {
      handleUpdateModalOpen(true);
    }
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;

  const name = currentUser?.nickname ? currentUser?.nickname : currentUser?.username;

  if (!currentUser || !name) {
    return loading;
  }

  const menuItems = [
    ...(menu
      ? [
          {
            key: 'center',
            icon: <UserOutlined />,
            label: '个人中心',
          },
          {
            key: 'settings',
            icon: <SettingOutlined />,
            label: '个人设置',
          },
          {
            type: 'divider' as const,
          },
        ]
      : []),
    {
      key: 'updating_password',
      icon: <LockOutlined />,
      label: '修改密码',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  return (
    <>
      <HeaderDropdown
        menu={{
          selectedKeys: [],
          onClick: onMenuClick,
          items: menuItems,
        }}
      >
        {children}
      </HeaderDropdown>
      <UpdatingUserPasswordForm
        handleModalOpen={handleUpdateModalOpen}
        handleUpdating={handUpdatingPassword}
        updatingModalOpen={updateModalOpen}
      />
    </>
  );
};
